import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { getPrimaryColor } from '@/lib/primary-color';

// Standalone t function for use outside React components
// This pattern allows Poedit to extract strings (recognizes t() calls)
const t = i18n.t.bind(i18n);

/**
 * Get feedback widget labels using i18n translations
 */
function getFeedbackLabels() {
	return {
		triggerLabel: t('Nápad alebo problém?'),
		formTitle: t('Nahlásenie problému'),
		submitButtonLabel: t('Odoslať'),
		cancelButtonLabel: t('Zrušiť'),
		nameLabel: t('Meno'),
		namePlaceholder: t('Vaše meno'),
		emailLabel: t('Email'),
		emailPlaceholder: t('email@example.com'),
		messageLabel: t('Popis problému'),
		messagePlaceholder: t('Čo sa stalo? Čo ste očakávali?'),
		successMessageText: t('Ďakujeme za nahlásenie!'),
		addScreenshotButtonLabel: t('Pridať snímku'),
		removeScreenshotButtonLabel: t('Odstrániť snímku'),
		isRequiredLabel: t('(povinné)'),
	};
}

/**
 * Initialize Sentry error tracking
 * Configuration is read from window.dddData.sentry (WordPress options)
 */
export function initSentry(): void {
	const config = window.dddData?.sentry;

	// Skip if not configured or disabled
	if (!config?.dsn || !config.enabled) {
		console.log('[Sentry] Disabled or not configured');
		return;
	}

	// Skip on localhost unless explicitly enabled
	const isLocalhost =
		window.location.hostname === 'localhost' ||
		window.location.hostname === '127.0.0.1' ||
		window.location.hostname.endsWith('.local');

	if (isLocalhost && !config.localhost) {
		console.log('[Sentry] Skipped on localhost (not enabled in settings)');
		return;
	}

	// Extract site URL from apiUrl
	const siteUrl =
		window.dddData?.apiUrl?.replace('/wp-json/ddd/v1', '') || window.location.origin;

	// Build integrations array dynamically
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const integrations: any[] = [];

	// User Feedback Widget (if enabled)
	if (config.feedback) {
		// Use DDD primary color for accent (submit button)
		const primaryColor = getPrimaryColor();

		integrations.push(
			Sentry.feedbackIntegration({
				// Localized UI labels (using i18n.t())
				...getFeedbackLabels(),
				// Features
				enableScreenshot: true,
				// Styling
				colorScheme: 'light',
				showBranding: false,
				autoInject: false, // Use custom trigger button in Sidebar instead
				// Theme - use DDD primary color for submit button
				themeLight: {
					accentBackground: primaryColor,
					accentForeground: '#ffffff',
					successColor: '#16a34a', // green-600
				},
				themeDark: {
					accentBackground: primaryColor,
					accentForeground: '#ffffff',
					successColor: '#22c55e', // green-500
				},
			})
		);
	}

	// Session Replay (if enabled)
	if (config.replay) {
		integrations.push(
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
			})
		);
	}

	// Initialize Sentry
	Sentry.init({
		dsn: config.dsn,
		environment: isLocalhost ? 'development' : 'production',
		release: `ddd-protocol@${window.dddData?.version || '0.0.0'}`,
		integrations,

		// Session Replay sampling (only if replay enabled)
		replaysSessionSampleRate: config.replay ? 0.1 : 0,
		replaysOnErrorSampleRate: config.replay ? 1.0 : 0,

		// Initial scope - tags for filtering
		initialScope: {
			tags: {
				site_url: siteUrl,
				plugin_version: window.dddData?.version,
			},
			user: window.dddData?.currentUser
				? {
						id: String(window.dddData.currentUser.id),
						username: window.dddData.currentUser.name,
						email: window.dddData.currentUser.email,
					}
				: undefined,
		},

		// Ignore common non-errors
		ignoreErrors: [
			'Network Error',
			'Failed to fetch',
			'NetworkError',
			'Load failed',
			'AbortError',
			'The user aborted a request',
			'ResizeObserver loop',
		],

		// Drop events when offline to prevent unnecessary network errors in console
		beforeSend(event) {
			if (!navigator.onLine) {
				console.log('[Sentry] Dropping event - offline');
				return null;
			}
			return event;
		},
	});

	// Expose test function for Debug page
	(window as { testSentryError?: () => void }).testSentryError = () => {
		Sentry.captureException(new Error('Test error from Debug page'));
		alert('Test error odoslaný do Sentry!');
	};

	console.log('[Sentry] Initialized for', siteUrl);
}

/**
 * Reinitialize Sentry feedback widget with new language labels.
 * Call this when the user changes the application language.
 */
export function reinitializeSentryFeedback(): void {
	const config = window.dddData?.sentry;

	// Skip if feedback not enabled
	if (!config?.feedback || !config.enabled) {
		return;
	}

	const feedback = Sentry.getFeedback();
	if (!feedback) {
		console.log('[Sentry] Feedback integration not found');
		return;
	}

	// Remove existing widget from DOM
	feedback.remove();

	// Create new widget with updated labels (uses current i18n language)
	const widget = feedback.createWidget({
		...getFeedbackLabels(),
		enableScreenshot: true,
		colorScheme: 'light',
		showBranding: false,
	});

	// Inject new widget into DOM
	widget.appendToDom();

	console.log('[Sentry] Feedback widget reinitialized for language:', i18n.language);
}

/**
 * Programmatically open Sentry feedback dialog.
 * Use as onClick handler for custom trigger button (SentryFeedbackButton).
 *
 * Uses createForm() to create a FeedbackDialog instance that can be opened.
 * @see https://docs.sentry.io/platforms/javascript/user-feedback/
 */
export async function openSentryFeedback(): Promise<void> {
	const config = window.dddData?.sentry;

	// Skip if feedback not enabled
	if (!config?.feedback || !config.enabled) {
		console.log('[Sentry] Feedback is not enabled');
		return;
	}

	const feedback = Sentry.getFeedback();
	if (!feedback) {
		console.log('[Sentry] Feedback integration not found');
		return;
	}

	try {
		// Create feedback form dialog with localized labels
		const form = await feedback.createForm({
			...getFeedbackLabels(),
			enableScreenshot: true,
			colorScheme: 'light',
			showBranding: false,
		});

		// Append to DOM and open the dialog
		form.appendToDom();
		form.open();

		console.log('[Sentry] Feedback dialog opened');
	} catch (error) {
		console.error('[Sentry] Failed to open feedback dialog:', error);
	}
}

// Re-export Sentry for use in components
export { Sentry };
